// shared/utils/dateHelpers.js
// Date and time utility functions

/**
 * Format date to readable string
 * @param {string|Date} date 
 * @param {string} format - 'short', 'long', 'relative'
 * @returns {string}
 */
export const formatDate = (date, format = 'short') => {
  const d = new Date(date);
  
  if (format === 'short') {
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
  
  if (format === 'long') {
    return d.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
  
  if (format === 'relative') {
    return getRelativeTime(d);
  }
  
  return d.toLocaleDateString();
};

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param {Date} date 
 * @returns {string}
 */
export const getRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - new Date(date);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHour < 24) return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
  if (diffDay < 30) {
    const weeks = Math.floor(diffDay / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  return formatDate(date, 'short');
};

/**
 * Get time greeting based on current hour
 * @returns {string}
 */
export const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

/**
 * Check if two dates are the same day
 * @param {Date|string} date1 
 * @param {Date|string} date2 
 * @returns {boolean}
 */
export const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.toDateString() === d2.toDateString();
};

/**
 * Check if date is today
 * @param {Date|string} date 
 * @returns {boolean}
 */
export const isToday = (date) => {
  return isSameDay(date, new Date());
};

/**
 * Check if date is yesterday
 * @param {Date|string} date 
 * @returns {boolean}
 */
export const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
};

/**
 * Get days between two dates
 * @param {Date|string} date1 
 * @param {Date|string} date2 
 * @returns {number}
 */
export const getDaysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get start of day
 * @param {Date|string} date 
 * @returns {Date}
 */
export const getStartOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get end of day
 * @param {Date|string} date 
 * @returns {Date}
 */
export const getEndOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Check if date is within last N days
 * @param {Date|string} date 
 * @param {number} days 
 * @returns {boolean}
 */
export const isWithinLastDays = (date, days) => {
  const d = new Date(date);
  const now = new Date();
  const daysDiff = getDaysBetween(d, now);
  return daysDiff <= days;
};

/**
 * Format duration in seconds to readable string
 * @param {number} seconds 
 * @returns {string}
 */
export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

/**
 * Get week number of the year
 * @param {Date|string} date 
 * @returns {number}
 */
export const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * Get month name
 * @param {number} monthIndex - 0-11
 * @param {string} format - 'short' or 'long'
 * @returns {string}
 */
export const getMonthName = (monthIndex, format = 'long') => {
  const date = new Date(2000, monthIndex, 1);
  return date.toLocaleDateString('en-US', { 
    month: format === 'short' ? 'short' : 'long' 
  });
};
